import type { TimingFeedback, TimingNoteFeedback } from '@tunasong/music-lib'
import type { FC } from 'react'
import { useVocalCopy } from '../pitch-training/use-vocal-copy.js'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import styles from './timing-feedback-panel.module.css'

/**
 * Everything the tool says about WHEN the notes were played.
 *
 * Not one sentence here is composed by this file. Each verdict is one §10 lexicon entry, chosen by
 * `toTimingFeedback` from the numbers `scoreTiming` measured, and rendered with its `{{ms}}` and
 * `{{r}}` filled through the never-leak-raw-token guard. The only strings this component owns are
 * the section label and a numeral.
 *
 * Order is the message. The tendency comes first because it is the finding a player can act on -
 * "you are ahead of the beat" is a practice instruction, while a row of per-note chips is a
 * transcript. The feel sentence comes last because it is the most specialised of the three and
 * only appears at all when the melody has a feel to match.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6.2, §10
 */

interface NoteChipProps {
  note: TimingNoteFeedback
  position: number
}

const NoteChip: FC<NoteChipProps> = ({ note, position }) => {
  const band = useVocalCopy(note.bandLexiconId, { ms: note.ms })
  return (
    <li className={styles.chip}>
      <span className={styles.chipIndex}>{position + 1}</span>
      {band ? (
        <span className={styles.chipBand} title={band.body}>
          <TheoryGlyphs>{band.tag}</TheoryGlyphs>
        </span>
      ) : null}
    </li>
  )
}

interface CopyLineProps {
  lexiconId: string
  ms?: number
  r?: number
  className: string
}

const CopyLine: FC<CopyLineProps> = ({ lexiconId, ms, r, className }) => {
  const copy = useVocalCopy(lexiconId, { ms, r })
  if (!copy) {
    return null
  }
  return (
    <p className={className}>
      <TheoryGlyphs>{copy.body}</TheoryGlyphs>
    </p>
  )
}

export interface TimingFeedbackPanelProps {
  /** Null when the aligner paired nothing - there is no timing to report, so nothing renders. */
  feedback: TimingFeedback | null
}

export const TimingFeedbackPanel: FC<TimingFeedbackPanelProps> = ({ feedback }) => {
  if (feedback === null) {
    return null
  }

  return (
    <section className={styles.timing} aria-label="Timing">
      {/*
        * No tendency when every paired note was displaced: there is no lean to name, and the
        * branch is explicit rather than leaning on `getLexiconEntry(null)` happening to return
        * nothing - an accident is not an intention a reader can rely on.
        */}
      {feedback.tendencyLexiconId ? (
        <CopyLine
          lexiconId={feedback.tendencyLexiconId}
          ms={feedback.tendencyMs ?? undefined}
          className={styles.headline}
        />
      ) : null}

      <ol className={styles.chips}>
        {feedback.perNote.map((note, position) => (
          <NoteChip key={position} note={note} position={position} />
        ))}
      </ol>

      {feedback.feelLexiconId ? (
        <CopyLine
          lexiconId={feedback.feelLexiconId}
          r={feedback.feelR ?? undefined}
          className={styles.body}
        />
      ) : null}
    </section>
  )
}
